import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, ArrowLeft, Download, Wrench, FileText, ClipboardList, UserCheck, FileCheck, ShieldCheck, ShieldAlert, ShieldX, MapPin, Calendar, User as UserIcon, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { generateReportPDF } from '../lib/pdf';
import FileUpload from '../components/FileUpload';

export default function BuildingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [ppm, setPpm] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, comp, pp, c] = await Promise.all([
        axios.get(`${API}/buildings/${id}/stats`, { withCredentials: true }),
        axios.get(`${API}/complaints?building_id=${id}`, { withCredentials: true }),
        axios.get(`${API}/ppm-tasks?building_id=${id}`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setStats(s.data); setComplaints(comp.data); setPpm(pp.data);
      setCompany(c.data.find(x => x.id === user?.company_id) || c.data[0]);
    } catch (e) {
      toast.error('Failed to load building');
    }
    setLoading(false);
  };

  const downloadPDF = () => {
    if (!stats) return;
    generateReportPDF({
      title: `${stats.building.name} — Building Report`,
      subtitle: stats.building.address,
      company,
      sections: [
        {
          heading: 'KPIs',
          head: ['Metric', 'Value'],
          rows: [
            ['Assets', stats.assets],
            ['Open Complaints', stats.complaints_open],
            ['In Progress', stats.complaints_in_progress],
            ['Resolved', stats.complaints_resolved],
            ['PPM Pending', stats.ppm_pending],
            ['PPM Completed', stats.ppm_completed],
            ['Tenants', stats.tenants],
            ['Valid Certificates', stats.certificates_valid],
          ]
        },
        {
          heading: 'Recent Complaints',
          head: ['Title', 'Status', 'Priority', 'Date'],
          rows: complaints.slice(0, 20).map(c => [c.title, c.status, c.priority, new Date(c.created_at).toLocaleDateString()])
        },
        {
          heading: 'PPM Tasks',
          head: ['Title', 'Frequency', 'Status', 'Due'],
          rows: ppm.slice(0, 20).map(t => [t.title, t.frequency, t.status, new Date(t.due_date).toLocaleDateString()])
        }
      ]
    });
  };

  if (loading) return <Layout><div className="py-12 text-center text-muted-foreground">Loading…</div></Layout>;
  if (!stats) return <Layout><div className="py-12 text-center text-muted-foreground">Building not found</div></Layout>;

  const b = stats.building;
  const kpis = [
    { label: 'Assets', value: stats.assets, icon: Wrench, color: 'text-teal-600 bg-teal-100' },
    { label: 'Open', value: stats.complaints_open, icon: FileText, color: 'text-red-600 bg-red-100' },
    { label: 'In Progress', value: stats.complaints_in_progress, icon: FileText, color: 'text-blue-600 bg-blue-100' },
    { label: 'Resolved', value: stats.complaints_resolved, icon: FileText, color: 'text-green-600 bg-green-100' },
    { label: 'PPM Pending', value: stats.ppm_pending, icon: ClipboardList, color: 'text-amber-600 bg-amber-100' },
    { label: 'PPM Completed', value: stats.ppm_completed, icon: ClipboardList, color: 'text-green-600 bg-green-100' },
    { label: 'Tenants', value: stats.tenants, icon: UserCheck, color: 'text-purple-600 bg-purple-100' },
    { label: 'Certificates', value: stats.certificates_valid, icon: FileCheck, color: 'text-cyan-600 bg-cyan-100' }
  ];

  return (
    <Layout>
      <div data-testid="building-detail-page" className="space-y-6">
        <Button data-testid="back-btn" onClick={() => navigate('/buildings')} variant="ghost" size="sm" className="mb-2"><ArrowLeft className="w-4 h-4 mr-1" />Back to Buildings</Button>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{b.name}</h1>
              <p className="text-sm text-muted-foreground">{b.address} · {b.floors} floors · {b.total_area?.toLocaleString()} sq ft</p>
            </div>
          </div>
          <Button data-testid="download-building-pdf" onClick={downloadPDF} variant="outline"><Download className="w-4 h-4 mr-1" />Download Report</Button>
        </div>

        {/* Compliance banner */}
        {(() => {
          const comp = stats.compliance_status || 'ok';
          const cfg = comp === 'expired'
            ? { Icon: ShieldX, label: 'Compliance issues — action required', cls: 'bg-rose-50 border-rose-300 text-rose-800' }
            : comp === 'expiring'
            ? { Icon: ShieldAlert, label: 'Compliance items expiring soon', cls: 'bg-amber-50 border-amber-300 text-amber-800' }
            : { Icon: ShieldCheck, label: 'All compliance items in good standing', cls: 'bg-emerald-50 border-emerald-300 text-emerald-800' };
          const I = cfg.Icon;
          return (
            <Card data-testid={`compliance-banner-${comp}`} className={`p-4 border-2 ${cfg.cls} flex flex-wrap items-center gap-4`}>
              <I className="w-6 h-6" />
              <div className="font-semibold">{cfg.label}</div>
              <div className="text-xs flex flex-wrap gap-3">
                <span data-testid="cert-expiring-count">Certificates expiring: <b>{stats.certificates_expiring || 0}</b></span>
                <span data-testid="cert-expired-count">Expired: <b>{stats.certificates_expired || 0}</b></span>
                <span>License: <b className="capitalize">{stats.license_status || 'n/a'}</b></span>
                <span>Insurance: <b className="capitalize">{stats.insurance_status || 'n/a'}</b></span>
              </div>
            </Card>
          );
        })()}

        {/* Building details */}
        <Card className="p-5">
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" />Building Information</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div><div className="text-xs text-muted-foreground">Owner</div><div className="font-medium flex items-center gap-1"><UserIcon className="w-3 h-3" />{b.owner_name || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Owner Contact</div><div className="font-medium">{b.owner_contact || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Year of Construction</div><div className="font-medium">{b.year_of_construction || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">License Number</div><div className="font-medium">{b.license_number || '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">License Validity</div><div className="font-medium flex items-center gap-1"><Calendar className="w-3 h-3" />{b.license_validity ? new Date(b.license_validity).toLocaleDateString() : '—'}</div></div>
            <div><div className="text-xs text-muted-foreground">Insurance</div><div className="font-medium">{b.insurance_provider || '—'}{b.insurance_expiry ? ` · exp ${new Date(b.insurance_expiry).toLocaleDateString()}` : ''}</div></div>
            <div><div className="text-xs text-muted-foreground">Location</div><div className="font-medium flex items-center gap-1"><MapPin className="w-3 h-3" />{(b.location_lat && b.location_lng) ? `${b.location_lat}, ${b.location_lng}` : '—'}</div></div>
          </div>
        </Card>

        {/* Photo gallery & documents */}
        <Card className="p-5">
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2"><ImageIcon className="w-4 h-4" />Photos & Documents</h3>
          <FileUpload
            value={b.gallery || []}
            onChange={async (urls) => {
              try {
                await axios.put(`${API}/buildings/${id}`, { gallery: urls }, { withCredentials: true });
                fetchData();
                toast.success('Gallery updated');
              } catch { toast.error('Failed to save'); }
            }}
            maxFiles={20}
          />
          {(b.gallery || []).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mt-3">
              {(b.gallery || []).filter(u => /\.(png|jpe?g|webp|gif)$/i.test(u)).map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" data-testid={`gallery-thumb-${i}`} className="block aspect-square rounded-lg overflow-hidden border border-slate-200 hover:border-amber-400 transition">
                  <img src={url} alt={`bldg-${i}`} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
        </Card>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {kpis.map((k, i) => {
            const Icon = k.icon;
            return (
              <Card key={i} data-testid={`kpi-${k.label.toLowerCase().replace(/ /g, '-')}`} className="p-4">
                <div className={`inline-flex p-2 rounded-lg mb-2 ${k.color}`}><Icon className="w-4 h-4" /></div>
                <div className="text-2xl font-bold text-foreground">{k.value}</div>
                <div className="text-xs text-muted-foreground">{k.label}</div>
              </Card>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <h3 className="font-semibold text-foreground mb-3">Recent Complaints</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {complaints.slice(0, 10).map(c => (
                <div key={c.id} className="flex justify-between items-start p-2 rounded hover:bg-slate-50">
                  <div>
                    <div className="text-sm font-medium">{c.title}</div>
                    <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'resolved' ? 'bg-green-100 text-green-800' : c.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>{c.status}</span>
                </div>
              ))}
              {complaints.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No complaints</p>}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="font-semibold text-foreground mb-3">PPM Tasks</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {ppm.slice(0, 10).map(t => (
                <div key={t.id} className="flex justify-between items-start p-2 rounded hover:bg-slate-50">
                  <div>
                    <div className="text-sm font-medium">{t.title}</div>
                    <div className="text-xs text-muted-foreground">Due {new Date(t.due_date).toLocaleDateString()} · {t.frequency}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{t.status}</span>
                </div>
              ))}
              {ppm.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No PPM tasks</p>}
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
